import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'common/lazy';

import { useClassName, useWindowSize } from 'common/hooks';
import { ContentfulRichText, SVGIcon } from 'common/components';
import { track } from 'utils/analytics';
import CompareTickets from 'public/components/ParkScreen/CompareTickets/CompareTickets';
import CloseIcon from 'common/assets/checkout-close.svg';
import {
  getInitialTicketOptionsTab,
  hasCheckoutLink,
  isTicketAdmission,
  resolveTicketOptions,
} from './helpers';

import './ticket-options-modal.less';

const TABS = [
  { id: 'tickets', label: 'Tickets' },
  { id: 'passes', label: 'Passes & Bundles' },
];

const matchesTab = (option, tabId) =>
  tabId === 'tickets' ? isTicketAdmission(option) : !isTicketAdmission(option);

function SegmentedControl({ classNames, activeTab, onChange }) {
  return (
    <div className={classNames('tabs')}>
      {TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={classNames('tab', activeTab === tab.id && 'active')}
          onClick={() => onChange(tab.id)}>
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function TicketCard({ classNames, option, selected, onSelect }) {
  return (
    <button
      type="button"
      className={classNames('card', selected && 'selected')}
      onClick={() => onSelect(option)}>
      {option.badge && (
        <span className={classNames('badge')} style={option.badge.style}>
          {option.badge.text}
        </span>
      )}
      <div className={classNames('card-media')}>
        {option.icon ? (
          <img alt={option.icon.assetAlt} src={option.icon.assetUrl} />
        ) : (
          <SVGIcon name="ticket" size="medium" />
        )}
      </div>
      <div className={classNames('info')}>
        <h2>{option.name}</h2>
        <ContentfulRichText field={option.description} />
        {option.infoBadge && (
          <span
            className={classNames('info-badge')}
            style={option.infoBadge.style}>
            {option.infoBadge.text}
          </span>
        )}
      </div>
      {(option.price || option.priceLabel) && (
        <div className={classNames('price')}>
          {option.priceLabel && (
            <span
              className={classNames('price-label')}
              style={option.priceLabelStyle}>
              {option.priceLabel}
            </span>
          )}
          {option.price && (
            <span className={classNames('price-amount')}>{option.price}</span>
          )}
        </div>
      )}
    </button>
  );
}

function TicketOptionsModalContent({
  onClose,
  options,
  venueName,
  ticketsCards,
  onClick,
}) {
  const classNames = useClassName('TicketOptionsModal');
  const { isMobile } = useWindowSize();
  const [activeTab, setActiveTab] = React.useState(() =>
    getInitialTicketOptionsTab(options)
  );
  const [selected, setSelected] = React.useState(null);
  const [compareOpen, setCompareOpen] = React.useState(false);
  const canCompare = Boolean(ticketsCards?.slides?.length);

  // Equalize both tabs to the height of the tallest one. Each tab list is
  // measured (the inactive one is absolutely positioned, see the styles) and
  // the container gets a min-height of the max. Width stays fully adaptive
  // because the active list flows normally instead of relying on a grid track.
  const stackRef = React.useRef(null);
  const [minHeight, setMinHeight] = React.useState(undefined);

  React.useLayoutEffect(() => {
    const stack = stackRef.current;
    if (!stack) return;
    const heights = Array.from(stack.children).map(
      (child) => child.scrollHeight
    );
    setMinHeight(heights.length ? Math.max(...heights) : undefined);
  }, [options, activeTab, isMobile]);

  const renderOption = (option) => {
    if (option.type === 'banner') {
      return (
        <a
          onClick={() => {
            track('Ticket Option Banner', {
              url: option.link,
              name: option.name,
            });
            onClick(option.link, option.name);
          }}
          href={option.link}
          key={option.name}>
          <img
            className={classNames('banner')}
            alt={isMobile ? option.mobile.assetAlt : option.desktop.assetAlt}
            src={isMobile ? option.mobile.assetUrl : option.desktop.assetUrl}
          />
        </a>
      );
    }

    return (
      <TicketCard
        key={option.name}
        classNames={classNames}
        option={option}
        selected={selected?.name === option.name}
        onSelect={setSelected}
      />
    );
  };

  return (
    <Modal.Content className={classNames('content')}>
      <div className={classNames('header')}>
        <div className={classNames('heading')}>
          {venueName && <p className={classNames('venue')}>{venueName}</p>}
          <h1>Select admission</h1>
        </div>
        <div className={classNames('close')} onClick={() => onClose()}>
          <img src={CloseIcon} alt="Close" />
        </div>
      </div>

      <SegmentedControl
        classNames={classNames}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      <div
        className={classNames('options-stack')}
        ref={stackRef}
        style={{ minHeight }}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <div
              key={tab.id}
              className={classNames('options', !isActive && 'inactive')}
              aria-hidden={!isActive}>
              {options
                .filter((option) => matchesTab(option, tab.id))
                .map((option) => renderOption(option))}
            </div>
          );
        })}
      </div>

      <div className={classNames('footer')}>
        {canCompare && (
          <p className={classNames('compare')}>
            Not sure what’s best for you?{' '}
            <a onClick={() => setCompareOpen(true)}>Compare tickets</a>
            <CompareTickets
              ticketsCards={ticketsCards}
              open={compareOpen}
              onClose={() => setCompareOpen(false)}
              trigger={null}
            />
          </p>
        )}
        {hasCheckoutLink(selected) ? (
          <a
            className={classNames('continue')}
            href={selected.link}
            onClick={() => onClick(selected.link, selected.name)}>
            Continue to Checkout
          </a>
        ) : (
          <button
            type="button"
            className={classNames('continue', 'disabled')}
            disabled>
            Continue to Checkout
          </button>
        )}
      </div>
    </Modal.Content>
  );
}

const TicketOptionsModal = ({
  trigger,
  isOpen,
  setOpen,
  options,
  venueName,
  venueSlug,
  ticketsCards,
  onClick,
}) => {
  const classNames = useClassName('TicketOptionsModal');
  const [internalOpen, setInternalOpen] = React.useState(false);

  const isControlled = isOpen !== undefined;
  const open = isControlled ? isOpen : internalOpen;
  const handleSet = isControlled ? setOpen : setInternalOpen;
  const resolvedOptions = resolveTicketOptions(
    options,
    ticketsCards,
    venueSlug
  );

  return (
    <Modal
      onOpen={() => handleSet(true)}
      onClose={() => handleSet(false)}
      trigger={trigger}
      className={classNames('container')}
      open={open}>
      <TicketOptionsModalContent
        options={resolvedOptions}
        venueName={venueName}
        ticketsCards={ticketsCards}
        onClick={onClick}
        onClose={() => handleSet(false)}
      />
    </Modal>
  );
};

TicketOptionsModal.propTypes = {
  trigger: PropTypes.any,
  isOpen: PropTypes.bool,
  setOpen: PropTypes.func,
  options: PropTypes.array,
  venueName: PropTypes.string,
  venueSlug: PropTypes.string,
  ticketsCards: PropTypes.object,
  onClick: PropTypes.func,
};

export default TicketOptionsModal;
